import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { DynamicFormArray, FIELD_TYPES, Props } from '../../model/form.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { StaffMasterTableSchema, StaffMasterFilterSchema, StaffMasterSearchConfig, StaffMasterDynamicOptionSchema, StaffMasterExportSearchConfig } from '../../model/staff-master.model';
import { LAYOUT_TYPE, OptionsFetchConfig } from '../../model/common.model';
import { BaseComponent } from '../base/base.component';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, ExportConfig, SearchConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { CommonService } from '../../services/common.service';
import { GlobalApiService } from '../../services/global-api.service';
import { PaginatorService } from '../../services/paginator-service.service';
import { Subject, takeUntil } from 'rxjs';
import { EXCEL_EXTENSION, ExcelExportService } from '../../services/excel-export.service';

@Component({
  selector: 'app-staff-master',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './staff-master.component.html',
  styleUrl: './staff-master.component.scss'
})
export class StaffMasterComponent extends BaseComponent implements OnDestroy {
  protected data = 'StaffMasterComponent';
  public override dataTableSchema: DataTableSchema = structuredClone(StaffMasterTableSchema);
  protected filterSchema: DynamicFormArray = structuredClone(StaffMasterFilterSchema);
  protected props: Props = {};
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  dynamicOptionFields: OptionsFetchConfig = StaffMasterDynamicOptionSchema;
  title = "";
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  globalApi = inject(GlobalApiService);
  changeRef = inject(ChangeDetectorRef);
  paginatorService = inject(PaginatorService);
  excelService = inject(ExcelExportService)
  private isFirstLoad = true;
  isDataInitialized = false;
  currentPage: any;
  public tableFilter = ''
  readonly destroy$ = new Subject<void>();
  override ngOnInit() {
    this.getData()
    super.ngOnInit();
    super.commonFunction();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback
    this.dataTableSchema.exportCallback = this.exportCallback
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] =
            this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      }
      else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
  }
  override  onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig()
    this.fetchAllData(searchConfig)
  };
  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig)
      }
    }
  }
  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true
    this.fetchAllData(searchConfig, true)
  }
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }
  override onResetClicked = () => {
    this.dataTableSchema.inputFilter = ''
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] }));
    this.resetDataTable();
    this.resetSorting()
    this.isDataInitialized = false;
    this.disableEnableButtons(false);
  };
  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          page: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }
  override updateOptions = () => { };
  override checkForAnyEmptyFields = () => { };
  getSearchConfig = () => {
    const searchConfig = structuredClone(StaffMasterSearchConfig)
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
          searchConfig['body'][field['fieldName']] = field['selectedValue']
        } else {
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = 0;
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if (this.sortBy && Object.keys(this.sortBy).length > 0) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig
  }
  fetchAllData = (_searchConfig: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    const searchConfig = structuredClone(_searchConfig)
    if (!callFromExport) {
      searchConfig['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchConfig['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    const exportQueryConfig = this.getExportConfig()
    const exportConfig: ExportConfig = {
      tableSchema: this.dataTableSchema,
      environment: ApiEnvNames['StaffMaster'],
      fileExtension: EXCEL_EXTENSION,
      searchParameters: StaffMasterExportSearchConfig,
      exportFileName: 'Staff_Master',
    };
    if (callFromExport) {
      searchConfig['body']['exportConfig'] = exportQueryConfig
    }
    this.service.getDataFromDataSource(searchConfig, ApiEnvNames['StaffMaster']).then(async response => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (callFromExport) {
          await this.service.downloadExcelFromS3(response.result[0], exportConfig);
        }
        else {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
           this.dataTableSchema.dataSource = response.result;
          if (!this.isDataInitialized && response.result.length > 0) {
            this.isDataInitialized = true;
          }
        }
      } else if (callFromExport) {
        await this.service.recursiveCallS3(exportQueryConfig, exportConfig);
      }
      else {
        this.setTableLoadingState(false);
        this.dataTableSchema.dataSource = [];
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }
      this.setTableLoadingState(false);
      this.changeRef.detectChanges();
      this.dataTableSchema.isLoading = false;
    }).catch(err => {
      this.setTableLoadingState(false);
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    }).finally(() => {
      this.setTableLoadingState(false);
    })
  }
  getData = () => {
    this.service.getDataFromDataSource(this.dynamicOptionFields.searchConfig, ApiEnvNames['StaffMaster']).then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          this.fillValuesFromApi(response.result);
        }
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }
      this.setTableLoadingState(false);
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      this.setTableLoadingState(false);
    })
  };
  fillValuesFromApi = (response: any) => {
    const fieldNames = new Set(this.dynamicOptionFields.fields.map(p => p.name));
    this.filterSchema.FieldsArray.forEach(filterField => {
      if (fieldNames.has(filterField['fieldName'])) {
        const fieldConfig = this.dynamicOptionFields.fields.find(p => p.name === filterField['fieldName']);
        if (fieldConfig) {
          const keyValuePair = this.globalApi.mapFieldValuesForDropDowns(response, fieldConfig.keyValuePair)//handle only for dealer number 
          filterField['options'] = keyValuePair;
          filterField['isLoading'] = false;
        }
      }
    })
  }

  formatDate(date: string | Date): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getExportConfig() {
    return {
      fileName: `Staff_Master_Report_Export_${this.commonUtils.getFormattedDate()}.csv`,
      bucketName: this.commonUtils.getBucketName()
    }
  }

}
